import { FeatureTypes } from "entity-app/constants";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ProjectWorkflowTemplateDropdown from "pages/preconstruction-details/components/workflow-template-dropdown";
import { Alert, Button, Select, Spin, Tooltip } from "antd";
import { useParams } from "react-router";
import {
  ErrorMessages,
  ESubmittalStatus,
  EWorkflowStatusDataBlock,
  ProjectPermissionEnum
} from "constants/index";
import DeletedSubmittalTag from "components/deleted-submittal-tag";
import ErrorBoundary from "components/error-boundary";
import VoidSubmittalTag from "components/void-submittal-tag";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { TEntityDateblock, TGoverningSource } from "./models";
import EntityDateblock from ".";
import NoSubmittalOrPlannedSubmittalLink from "./components/no-submittal-or-planned-submittal-link";
import MoveTrackedSubmittalModal from "pages/submittal-log-untracked/modals/move-tracked-submittal-modal";
import { getImplicitMaterialDisplayTitle } from "./utils/implicit-material-title";

type TFeatureInstance = TEntityDateblock & {
  title: string;
  feature_type_id: FeatureTypes;
  implicit: boolean;
};

type TLink = {
  governing_link: boolean;
  link_type: number;
  offset_duration?: any;
  linked_offset_date?: any;
  reverse_dependency?: boolean;
  source_feature_instance: TFeatureInstance;
  target_feature_instance: TFeatureInstance;
};

const getLinkedEntityTypeLabel = (
  featureTypeId: FeatureTypes
): TGoverningSource["type"] =>
  featureTypeId === FeatureTypes.MATERIALS
    ? "Linked Material"
    : "Linked Activity";

export const resolveSubmittalGoverningSource = (
  submittalEntity?: TEntityDateblock
): TGoverningSource | undefined => {
  if (!submittalEntity) return undefined;

  const governingTask = submittalEntity?.links_as_source?.find(
    (x) => x.governing_link
  );
  if (governingTask) {
    const governingEntity = governingTask?.target_feature_instance;
    return {
      id: governingEntity?.feature_workflow_instance.feature_instance_id,
      name: governingEntity?.implicit
        ? getImplicitMaterialDisplayTitle(submittalEntity?.auto_inc_value)
        : governingEntity?.title,
      type: getLinkedEntityTypeLabel(governingEntity?.feature_type_id),
      isLinkedEndDate: governingTask?.link_type === 2
    };
  }
  if (submittalEntity?.feature_workflow_instance?.manual_entry_date) {
    return {
      id: submittalEntity.feature_workflow_instance.feature_instance_id,
      name: "Manually Set Date",
      type: "Manually Set Date" as const
    };
  }
  return undefined;
};

const isSubmittalLinkInProgress = (link: TLink) =>
  link?.source_feature_instance?.feature_type_id === FeatureTypes.SUBMITTALS &&
  [
    EWorkflowStatusDataBlock.IN_PROGRESS.toString(),
    EWorkflowStatusDataBlock.WF_NOT_STARTED.toString()
  ].includes(
    link?.source_feature_instance?.feature_workflow_instance?.workflow_status
  );

export const resolveMaterialGoverningSource = (
  materialEntity?: TEntityDateblock
) => {
  if (!materialEntity) {
    return {
      governingSource: undefined,
      isDependedEntityInProgress: false
    };
  }

  const isAnySubmittalNotClosed = (
    materialEntity?.links_as_target as Array<TLink>
  )?.some(isSubmittalLinkInProgress);

  const governingTask = (materialEntity?.links_as_source as Array<TLink>)?.find(
    (x) => x.governing_link
  );

  if (governingTask) {
    return {
      governingSource: {
        id: governingTask?.target_feature_instance?.feature_workflow_instance
          .feature_instance_id,
        name: governingTask?.target_feature_instance?.title,
        type: getLinkedEntityTypeLabel(
          governingTask?.target_feature_instance?.feature_type_id
        ),
        isLinkedEndDate: governingTask?.link_type === 2,
        offsetDuration: governingTask?.offset_duration,
        linkedOffsetDate: governingTask?.linked_offset_date,
        reverseDependency: governingTask?.reverse_dependency
      },
      isDependedEntityInProgress: !!isAnySubmittalNotClosed
    };
  }

  if (materialEntity?.feature_workflow_instance?.manual_entry_date) {
    return {
      governingSource: {
        id: materialEntity?.feature_workflow_instance?.feature_instance_id,
        name: "",
        type: "Manually Set Date" as const
      },
      isDependedEntityInProgress: !!isAnySubmittalNotClosed
    };
  }

  return {
    governingSource: undefined,
    isDependedEntityInProgress: !!isAnySubmittalNotClosed
  };
};

const filterNonImplicitMaterialLinks = (materialLinks: Array<TLink>) => {
  if (materialLinks.length <= 1) return materialLinks;
  return materialLinks.filter((x) => !x.target_feature_instance.implicit);
};

const resolveSubmittalViewLinks = (featureInstanceData: any) => {
  const linkedFeatures = featureInstanceData?.links_as_source as Array<TLink>;
  let materialLinks =
    linkedFeatures?.filter(
      (x) =>
        x.target_feature_instance?.feature_type_id === FeatureTypes.MATERIALS
    ) ?? [];

  materialLinks = filterNonImplicitMaterialLinks(materialLinks);

  let materialEntity: TEntityDateblock | undefined;
  if (materialLinks.length) {
    const governing = materialLinks.find((x) => x.governing_link);
    materialEntity = (governing || materialLinks[0]).target_feature_instance;
  }

  return {
    materialEntity,
    submittalEntity: featureInstanceData,
    materialLinks,
    submittalLinks: undefined
  };
};

const resolveMaterialViewLinks = (featureInstanceData: any) => {
  const linkedFeatures = featureInstanceData?.links_as_target as Array<TLink>;
  const submittalLinks = linkedFeatures?.filter(
    (x) =>
      x.source_feature_instance?.feature_type_id === FeatureTypes.SUBMITTALS
  );
  const submittalEntity = submittalLinks?.length
    ? submittalLinks[0].source_feature_instance
    : undefined;

  return {
    materialEntity: featureInstanceData as TEntityDateblock,
    submittalEntity,
    materialLinks: undefined,
    submittalLinks
  };
};

const resolveLinkedEntities = (
  featureInstanceData: any,
  isSubmittal: boolean
) =>
  isSubmittal
    ? resolveSubmittalViewLinks(featureInstanceData)
    : resolveMaterialViewLinks(featureInstanceData);

const findSubmittalInLinks = (
  submittalLinks: Array<TLink> | undefined,
  featureInstanceId: string | undefined
): TEntityDateblock | undefined => {
  if (!featureInstanceId || !submittalLinks?.length) return undefined;
  return submittalLinks.find(
    (x) =>
      x.source_feature_instance.feature_workflow_instance
        .feature_instance_id === featureInstanceId
  )?.source_feature_instance;
};

const findMaterialInLinks = (
  materialLinks: Array<TLink> | undefined,
  featureInstanceId: string | undefined
): TEntityDateblock | undefined => {
  if (!featureInstanceId || !materialLinks?.length) return undefined;
  return materialLinks.find(
    (x) =>
      x.target_feature_instance.feature_workflow_instance
        .feature_instance_id === featureInstanceId
  )?.target_feature_instance;
};

const canEditSubmittalOffset = (submittalEntity?: TEntityDateblock) => {
  if (!submittalEntity) return false;
  const featureSubmittal: any =
    submittalEntity?.feature_submittals?.length &&
    submittalEntity?.feature_submittals[0];

  return (
    !submittalEntity?.deleted_from_source &&
    featureSubmittal?.workflow_status &&
    featureSubmittal?.workflow_status !== ESubmittalStatus.VOID
  );
};

function submittalDateblockPermissions(flags: {
  effectiveDisable: boolean;
  isSubmittal: boolean;
  cannotChangeProjectedDate: boolean;
  cannotChangeDurationSubmittalDB: boolean;
  canSubmittalOffsetBeEdited: boolean;
  cannotChangePlannedDate: boolean;
}) {
  if (flags.effectiveDisable) return {};

  return {
    canEditActualDate: false,
    canEditProjectedDate: flags.isSubmittal && !flags.cannotChangeProjectedDate,
    canEditCommittedDate: flags.isSubmittal && !flags.cannotChangeProjectedDate,
    canEditDurations:
      !flags.cannotChangeDurationSubmittalDB &&
      flags.canSubmittalOffsetBeEdited,
    canEditPlannedDate: !flags.cannotChangePlannedDate,
    isDependedEntityInProgress: false,
    arePredecessorsComplete: true
  };
}

function materialDateblockPermissions(flags: {
  effectiveDisable: boolean;
  isSubmittal: boolean;
  cannotChangeActualDate: boolean;
  cannotChangeProjectedDate: boolean;
  cannotChangeDurationMaterialDB: boolean;
  cannotChangePlannedDate: boolean;
  isMaterialDependedEntityInProgress: boolean;
  arePredecessorsComplete: boolean;
}) {
  if (flags.effectiveDisable) return {};

  return {
    canEditActualDate: !flags.isSubmittal && !flags.cannotChangeActualDate,
    canEditProjectedDate:
      !flags.isSubmittal && !flags.cannotChangeProjectedDate,
    canEditCommittedDate:
      !flags.isSubmittal && !flags.cannotChangeProjectedDate,
    canEditDurations: !flags.cannotChangeDurationMaterialDB,
    canEditPlannedDate: !flags.cannotChangePlannedDate,
    isDependedEntityInProgress: flags.isMaterialDependedEntityInProgress,
    arePredecessorsComplete: flags.arePredecessorsComplete
  };
}

function NoLinkedMaterialsPanel({
  submittalEntity,
  canEditLinkingPage,
  projectId
}: Readonly<{
  submittalEntity: TEntityDateblock;
  canEditLinkingPage: boolean;
  projectId: string;
}>) {
  const isMaterialTrackingOn =
    submittalEntity?.feature_submittals?.[0]?.material_tracking;

  return (
    <div
      className="no-linking-item-found"
      data-testid="dsme-no-materials-container"
    >
      <div
        className="no-linking-item-found--title text-center"
        data-testid="dsme-no-materials-title"
      >
        {isMaterialTrackingOn
          ? "No Materials Linked"
          : "Material tracking is disabled"}
      </div>
      {canEditLinkingPage && (
        <Button
          type="link"
          disabled={!canEditLinkingPage}
          href={`/project/${projectId}/schedule/submittal-linking?&SubmittalId=${submittalEntity.feature_workflow_instance.feature_instance_id}`}
          data-testid="dsme-link-materials-button"
        >
          {isMaterialTrackingOn ? "Click here to add" : "Click here to enable"}
        </Button>
      )}
    </div>
  );
}

function SubmittalLinksSelect({
  submittalLinks,
  submittalEntity,
  onSelectSubmittal
}: Readonly<{
  submittalLinks: Array<TLink> | undefined;
  submittalEntity: TEntityDateblock | undefined;
  onSelectSubmittal: (entity: TEntityDateblock | undefined) => void;
}>) {
  if (!submittalLinks || submittalLinks.length === 0) {
    return null;
  }

  return (
    <div
      className="flex space-x-2 items-center"
      data-testid="dsme-submittals-dropdown-container"
    >
      <div
        className="text-xs font-bold tracking-wider"
        data-testid="dsme-submittals-label"
      >
        Submittals:
      </div>
      <Select
        className="w-96"
        options={submittalLinks?.map((x) => ({
          label: (
            <Tooltip
              title={x?.source_feature_instance?.title}
              data-testid="dsme-submittal-tooltip"
            >
              <div className="flex items-center space-x-1 truncate whitespace-nowrap">
                <div data-testid="dsme-submittal-title" className="truncate">
                  {x?.source_feature_instance?.title}
                </div>
                {x?.source_feature_instance?.deleted_from_source && (
                  <span className="flex items-center ml-2">
                    <DeletedSubmittalTag />
                  </span>
                )}
                {x?.source_feature_instance?.feature_workflow_instance
                  ?.workflow_status ===
                  (EWorkflowStatusDataBlock.VOID as string) && (
                  <span className="flex items-center ml-2">
                    <VoidSubmittalTag />
                  </span>
                )}
              </div>
            </Tooltip>
          ),
          value:
            x?.source_feature_instance?.feature_workflow_instance
              ?.feature_instance_id
        }))}
        value={submittalEntity?.feature_workflow_instance?.feature_instance_id}
        onChange={(feature_instance_id) => {
          const currentSubmittal = submittalLinks?.find(
            (x) =>
              x.source_feature_instance.feature_workflow_instance
                .feature_instance_id === feature_instance_id
          );
          onSelectSubmittal(currentSubmittal?.source_feature_instance);
        }}
        data-testid="dsme-submittals-select"
      />
    </div>
  );
}

function MaterialLinksSelect({
  materialLinks,
  materialEntity,
  sequenceId,
  onSelectMaterial
}: Readonly<{
  materialLinks: Array<TLink> | undefined;
  materialEntity: TEntityDateblock | undefined;
  sequenceId: any;
  onSelectMaterial: (entity: TEntityDateblock | undefined) => void;
}>) {
  if (!materialLinks || materialLinks.length === 0) {
    return null;
  }

  return (
    <div
      className="flex space-x-2 items-center"
      data-testid="dsme-materials-dropdown-container"
    >
      <div
        className="text-xs font-bold tracking-wider"
        data-testid="dsme-materials-label"
      >
        Material:
      </div>
      <Select
        className="w-96"
        options={materialLinks?.map((x) => {
          const materialName = x.target_feature_instance.implicit
            ? getImplicitMaterialDisplayTitle(sequenceId)
            : x.target_feature_instance.title;
          return {
            label: (
              <Tooltip title={materialName} data-testid="dsme-material-tooltip">
                <div className="flex items-center space-x-1 truncate whitespace-nowrap">
                  {x.governing_link && (
                    <div data-testid="dsme-governing-indicator">*</div>
                  )}
                  <div data-testid="dsme-material-title" className="truncate">
                    {materialName}
                  </div>
                </div>
              </Tooltip>
            ),
            value:
              x?.target_feature_instance?.feature_workflow_instance
                ?.feature_instance_id
          };
        })}
        value={materialEntity?.feature_workflow_instance?.feature_instance_id}
        onChange={(feature_instance_id) => {
          const currentMaterial = materialLinks?.find(
            (x) =>
              x.target_feature_instance.feature_workflow_instance
                .feature_instance_id === feature_instance_id
          );
          onSelectMaterial(currentMaterial?.target_feature_instance);
        }}
        data-testid="dsme-materials-select"
      />
    </div>
  );
}

function DateblockSubmittalMaterialEntity(
  props: Readonly<{
    featureInstanceData: any;
    afterChange?: () => void;
    disableDB?: boolean;
    isRecalculating?: boolean;
    onRecalculatingChange?: (val: boolean) => void;
  }>
) {
  const { projectId } = useParams() as any;
  const {
    tokenContents,
    columnHeaders: { submittalHeaderMap, materialHeaderMap }
  } = useContext(ProjectContext);
  const {
    featureInstanceData,
    afterChange,
    disableDB,
    isRecalculating,
    onRecalculatingChange
  } = props;
  const sequenceId = featureInstanceData?.auto_inc_value;

  const [tempDisable, setTempDisable] = useState(false);
  const effectiveDisable = tempDisable || !!isRecalculating;

  const handleApiCallLoading = (isLoading: boolean) => {
    setTempDisable(isLoading);
    onRecalculatingChange?.(isLoading);
  };
  const [materialEntity, setMaterialEntity] = useState<TEntityDateblock>();
  const [materialGoverningSource, setMaterialGoverningSource] =
    useState<TGoverningSource>();
  const [submittalGoverningSource, setSubmittalGoverningSource] =
    useState<TGoverningSource>();
  const [submittalEntity, setSubmittalEntity] = useState<TEntityDateblock>();
  const [showMoveTrackedSubmittalModal, setShowMoveTrackedSubmittalModal] =
    useState(false);

  const isSubmittal = featureInstanceData?.feature?.feature === "SUBMITTALS";
  const [submittalLinks, setSubmittalLinks] = useState<Array<TLink>>();
  const [materialLinks, setMaterialLinks] = useState<Array<TLink>>();
  const [
    isMaterialDependedEntityInProgress,
    setMaterialDependedEntityInProgress
  ] = useState(false);
  const prevFeatureIdRef = useRef<string>();

  const isVoidEntity =
    featureInstanceData?.feature_workflow_instance?.workflow_status ===
    EWorkflowStatusDataBlock.VOID;
  const isDeletedEntity = featureInstanceData?.deleted_from_source;

  const cannotChangeDurationSubmittalDB =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.EditDateBlockOffsetSubmittal,
      tokenContents?.role ?? ""
    );

  const cannotEditUnTracked = isPermissionNotGrantted(
    ProjectPermissionEnum.EditUnTracked,
    tokenContents?.role ?? ""
  );

  const cannotChangeDurationMaterialDB =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.EditMaterialDBOffset,
      tokenContents?.role ?? ""
    );

  const cannotChangePlannedDate =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.changeMaterialPlannedDate,
      tokenContents?.role ?? ""
    );

  const cannotChangeActualDate =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.changeMaterialActualDate,
      tokenContents?.role ?? ""
    );

  const cannotChangeProjectedDate =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.EditDateblockProjectedDate,
      tokenContents?.role ?? ""
    );

  const canSubmittalOffsetBeEdited = useMemo(
    () => canEditSubmittalOffset(submittalEntity),
    [submittalEntity]
  );

  const submittalToTrack = useMemo(() => {
    if (!featureInstanceData?.is_tracked) return featureInstanceData;
    return null;
  }, [featureInstanceData]);

  const arePredecessorsComplete = useMemo(() => {
    if (featureInstanceData?.feature_type_id === FeatureTypes.MATERIALS) {
      const featureMaterialInstance =
        featureInstanceData?.feature_materials?.[0] || ({} as any);
      return featureMaterialInstance?.ready_for_release || false;
    }
    return true;
  }, [
    featureInstanceData?.feature_type_id,
    featureInstanceData?.feature_materials
  ]);

  useEffect(() => {
    setSubmittalGoverningSource(
      resolveSubmittalGoverningSource(submittalEntity)
    );
  }, [submittalEntity]);

  useEffect(() => {
    const { governingSource, isDependedEntityInProgress } =
      resolveMaterialGoverningSource(materialEntity);
    setMaterialGoverningSource(governingSource);
    setMaterialDependedEntityInProgress(isDependedEntityInProgress);
  }, [materialEntity]);

  useEffect(() => {
    const {
      materialEntity: nextMaterialEntity,
      submittalEntity: nextSubmittalEntity,
      materialLinks: nextMaterialLinks,
      submittalLinks: nextSubmittalLinks
    } = resolveLinkedEntities(featureInstanceData, isSubmittal);

    const featureId = featureInstanceData?.id;
    const isSameRootEntity = featureId === prevFeatureIdRef.current;
    prevFeatureIdRef.current = featureId;

    setMaterialLinks(nextMaterialLinks);
    setSubmittalLinks(nextSubmittalLinks);

    if (isSubmittal && isSameRootEntity) {
      const selectedMaterialId =
        materialEntity?.feature_workflow_instance?.feature_instance_id;
      setMaterialEntity(
        findMaterialInLinks(nextMaterialLinks, selectedMaterialId) ??
          nextMaterialEntity
      );
    } else {
      setMaterialEntity(nextMaterialEntity);
    }

    if (!isSubmittal && isSameRootEntity) {
      const selectedId =
        submittalEntity?.feature_workflow_instance?.feature_instance_id;
      setSubmittalEntity(
        findSubmittalInLinks(nextSubmittalLinks, selectedId) ??
          nextSubmittalEntity
      );
    } else {
      setSubmittalEntity(nextSubmittalEntity);
    }
    // materialEntity / submittalEntity intentionally omitted — read only on featureInstanceData refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureInstanceData, isSubmittal]);

  const materialTemplateChangeDropdown = (
    <div
      className="flex items-center space-x-1 font-medium"
      data-testid="dsme-template-dropdown-container"
    >
      <div
        className="text-xs font-bold tracking-wider"
        data-testid="dsme-template-label"
      >
        Material Template:
      </div>
      {materialEntity && (
        <ProjectWorkflowTemplateDropdown
          className="w-96"
          afterChange={afterChange}
          featureId={FeatureTypes.MATERIALS}
          featureWorkflowInstance={materialEntity.feature_workflow_instance}
          setApiCallLoading={handleApiCallLoading}
          disable={effectiveDisable}
          data-testid="dsme-template-dropdown"
        />
      )}
    </div>
  );

  const connectorLine = (
    <div className="pt-4" data-testid="dsme-connector-line">
      <div className="h-[7.25rem] w-0" data-testid="dsme-connector-vertical" />

      <div className="relative w-0 h-0" data-testid="dsme-connector-junction">
        <div
          className="absolute w-[117px] -left-[117px] border-0 border-t border-solid shrink-0 grow"
          data-testid="dsme-connector-left"
        />
        <div
          className="absolute w-0 left-0 h-28 -top-10 border-0 border-r border-solid shrink-0 grow"
          data-testid="dsme-connector-up"
        />
        <div
          className="absolute w-[85px] border-0 border-t border-solid shrink-0 grow"
          data-testid="dsme-connector-right"
        />
      </div>
    </div>
  );

  const canEditLinkingPage =
    !disableDB &&
    !isPermissionNotGrantted(
      ProjectPermissionEnum.EditLinkingPage,
      tokenContents?.role ?? ""
    );

  const noLinkedSubmittalDBDiv = materialEntity && (
    <NoSubmittalOrPlannedSubmittalLink
      canEditLinkingPage={canEditLinkingPage}
      projectId={projectId}
      materialEntity={materialEntity}
      afterChange={afterChange}
      isRecalculating={effectiveDisable}
    />
  );

  const noLinkedMaterialItemDiv = submittalEntity?.feature_submittals
    ?.length && (
    <NoLinkedMaterialsPanel
      submittalEntity={submittalEntity}
      canEditLinkingPage={canEditLinkingPage}
      projectId={projectId}
    />
  );

  if (!submittalEntity && !materialEntity)
    return (
      <div
        className="flex h-full min-h-96 w-full min-w-[500px] justify-center items-center"
        data-testid="dsme-loading-container"
      >
        <Spin data-testid="dsme-loading-spinner" />
      </div>
    );

  const showvoidSubmittalBanner = () => {
    return (
      <div data-testid="dsme-void-banner-content">
        This is <strong>Voided Submittal.</strong> Unlink Materials and
        Activities linked to this Submittal.
      </div>
    );
  };

  const showDeletedSubmittalBanner = () => {
    return (
      <div data-testid="dsme-deleted-banner-content">
        This is a <strong>Deleted Submittal.</strong> Unlink Materials and
        Activties linked to this Submittal.
      </div>
    );
  };

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col"
      data-testid="dsme-root"
    >
      {isVoidEntity && (
        <Alert
          className="border-0 text-center bg-ciq-void-100 top-0 left-0 w-full"
          title={showvoidSubmittalBanner()}
          type="info"
          data-testid="dsme-void-alert"
        />
      )}

      {isDeletedEntity && (
        <Alert
          className="border-0 text-center bg-ciq-void-100 top-0 left-0 w-full"
          title={showDeletedSubmittalBanner()}
          type="info"
          data-testid="dsme-deleted-alert"
        />
      )}
      {!featureInstanceData?.is_tracked && (
        <Alert
          className="border-0 text-center bg-ciq-blue-100 top-0 left-0 w-full"
          title={
            /* antd v6 renamed alert internals (ant-alert-message -> ant-alert-title), so tag the message element with a class-independent test hook */
            <div
              className="text-black text-opacity-90 flex space-x-2 items-center justify-center"
              data-testid="excluded-from-tracking-alert-message"
            >
              <span>
                This Submittal is excluded from planning and tracking.
              </span>
              <Button
                type="primary"
                className="font-normal"
                loading={tempDisable}
                onClick={() => setShowMoveTrackedSubmittalModal(true)}
                disabled={!submittalToTrack || cannotEditUnTracked}
                title={
                  cannotEditUnTracked
                    ? ErrorMessages.PermissionNotGranted
                    : undefined
                }
              >
                Start Tracking
              </Button>
            </div>
          }
          type="info"
          data-testid="dsme-not-tracked-alert"
        />
      )}

      <div
        className="flex min-h-0 flex-1 items-start overflow-x-auto overflow-y-hidden px-1 p-6"
        data-testid="dsme-main-content"
      >
        <div className="flex flex-col" data-testid="dsme-submittal-section">
          <div className="h-10 grow px-6" data-testid="dsme-submittal-header">
            {!isSubmittal && (
              <SubmittalLinksSelect
                submittalLinks={submittalLinks}
                submittalEntity={submittalEntity}
                onSelectSubmittal={setSubmittalEntity}
              />
            )}
          </div>
          {submittalEntity ? (
            <ErrorBoundary>
              <EntityDateblock
                entity={submittalEntity}
                afterChange={afterChange}
                governingSource={submittalGoverningSource}
                permissions={submittalDateblockPermissions({
                  effectiveDisable,
                  isSubmittal,
                  cannotChangeProjectedDate,
                  cannotChangeDurationSubmittalDB,
                  canSubmittalOffsetBeEdited,
                  cannotChangePlannedDate
                })}
                setApiCallLoading={handleApiCallLoading}
                entityHeaders={submittalHeaderMap}
                data-testid="dsme-submittal-dateblock"
              />
            </ErrorBoundary>
          ) : (
            noLinkedSubmittalDBDiv
          )}
        </div>
        {submittalEntity && materialEntity && connectorLine}

        <div className="flex flex-col" data-testid="dsme-material-section">
          <div
            className="h-10 flex items-center space-x-2 px-5"
            data-testid="dsme-material-header"
          >
            {isSubmittal ? (
              <MaterialLinksSelect
                materialLinks={materialLinks}
                materialEntity={materialEntity}
                sequenceId={sequenceId}
                onSelectMaterial={setMaterialEntity}
              />
            ) : (
              materialTemplateChangeDropdown
            )}
          </div>
          {materialEntity ? (
            <ErrorBoundary>
              <EntityDateblock
                entity={materialEntity}
                afterChange={afterChange}
                governingSource={materialGoverningSource}
                permissions={materialDateblockPermissions({
                  effectiveDisable,
                  isSubmittal,
                  cannotChangeActualDate,
                  cannotChangeProjectedDate,
                  cannotChangeDurationMaterialDB,
                  cannotChangePlannedDate,
                  isMaterialDependedEntityInProgress,
                  arePredecessorsComplete
                })}
                setApiCallLoading={handleApiCallLoading}
                entityHeaders={materialHeaderMap}
                data-testid="dsme-material-dateblock"
              />
            </ErrorBoundary>
          ) : (
            noLinkedMaterialItemDiv
          )}
        </div>
      </div>

      <MoveTrackedSubmittalModal
        open={showMoveTrackedSubmittalModal}
        onClose={() => setShowMoveTrackedSubmittalModal(false)}
        onSuccess={() => {
          setShowMoveTrackedSubmittalModal(false);
          afterChange?.();
        }}
        featureInstances={submittalToTrack ? [submittalToTrack] : []}
      />
    </div>
  );
}

export default DateblockSubmittalMaterialEntity;
